/**
 * Shared collection (הנהלת חשבונות → ניהול גביה) types, option lists and the
 * status-derivation algorithms.
 *
 * The Collection screen ingests one of four monthly Excel exports and derives
 * each customer's collection status from it. The four excel types are exactly
 * the customer `PAYMENT_METHODS`, and the resulting statuses are the existing
 * `COLLECTION_*` labels — both re-used from `customer.types` so the domain
 * vocabulary lives in a single place.
 *
 * This file is dependency-free (no React/MUI/browser imports) so the backend
 * can import it via the `@malash-office/types/collection` deep alias.
 */
import type { CustomerTag } from './customer.types';
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_OPTIONS,
  COLLECTION_AUTO,
  COLLECTION_TO_COLLECT,
  COLLECTION_NO_COLLECT,
  COLLECTION_CREDIT,
  SITE_STATUS_OPEN,
  SITE_STATUS_TO_CLOSE,
  SITE_STATUS_CLOSED,
  SITE_STATUS_TO_OPEN,
  SITE_STATUS_VIP
} from './customer.types';

// --- Excel types (= payment methods) ---
export const [EXCEL_TYPE_HORAAT_KEVA, EXCEL_TYPE_CREDIT, EXCEL_TYPE_CUSTOMERS, EXCEL_TYPE_CALCULATED] = PAYMENT_METHODS;
export const COLLECTION_EXCEL_TYPES = PAYMENT_METHODS;
export const EXCEL_TYPE_OPTIONS = PAYMENT_METHOD_OPTIONS;

// --- Aging buckets (עמודת "תקופה" באקסל המחושב) ---
export const AGING_BUCKETS = ['1 - 30', '31 - 60', '61 - 90', '> 90'] as const;

/**
 * Classify an aging-period label as "over 60 days" by the numeric lower bound
 * of the bucket (≥ 61). Whitespace/format-insensitive — '61 - 90', '61-90' and
 * '> 90' are all over-60; '1 - 30' / '31 - 60' are not.
 */
export const isOver60Bucket = (periodLabel: string): boolean => {
  const lower = (periodLabel.match(/\d+/g) ?? []).map(Number)[0];
  return typeof lower === 'number' && lower >= 61;
};

// --- Status algorithms ---

/**
 * Per-row status for the simple excel types (positive = owes, negative = בזכות):
 * - owes  → `debtorStatus` (גביה אוטומטית for הו"ק/אשראי, לגביה for לקוחות)
 * - credit → לקוח בזכות
 * - zero  → לא לגביה
 */
export const computeSimpleStatus = (amount: number, debtorStatus: string): string => {
  if (amount > 0) return debtorStatus;
  if (amount < 0) return COLLECTION_CREDIT;
  return COLLECTION_NO_COLLECT;
};

/** The status a debtor (positive amount) gets, per excel type. */
export const debtorStatusForExcelType = (excelType: string): string =>
  excelType === EXCEL_TYPE_CUSTOMERS ? COLLECTION_TO_COLLECT : COLLECTION_AUTO;

export interface AgingTotals {
  /** The 61-90 and >90 buckets, **after** `applyCreditsOldestFirst` — never negative. */
  over60: number;
  /** The 1-30 and 31-60 buckets, after the credits above; negative = the account is in זכות. */
  under60: number;
}

/**
 * The raw sheet, split into debt and credit per aging region. Debts and credits are kept apart
 * (rather than summed per region) because a credit must be offset against the *oldest* debt
 * first — netting each region on its own would let a new purchase swallow the credit before it
 * ever reaches the old debt. See `applyCreditsOldestFirst`.
 */
export interface AgingBuckets {
  /** Σ of the positive 61-90 / >90 rows. */
  over60Debt: number;
  /** Σ of the negative 61-90 / >90 rows, as a positive magnitude. */
  over60Credit: number;
  /** Σ of the positive 1-30 / 31-60 rows. */
  under60Debt: number;
  /** Σ of the negative 1-30 / 31-60 rows, as a positive magnitude. */
  under60Credit: number;
}

/**
 * Offset every credit on the account against the oldest debt first ("זיכוי תמיד הולך אחורה"),
 * and only then against the newer buckets — the accounting rule the whole מחושבים screen rests on.
 *
 * The bucket a credit is *filed* under says nothing about what it pays off: Priority puts a credit
 * note in the period it was issued, so a זיכוי received this month lands in 31-60 or 1-30 while the
 * debt it settles sits in >90. Summing each region on its own (the old behaviour) therefore let a
 * fresh purchase absorb the credit and left the old debt standing — a customer who had already paid
 * kept showing up לגביה for debt over 60 days.
 *
 * Example — the account that surfaced the bug (1-30: 4340, 31-60: -3131, 61-90: 390, >90: 1217):
 * the 3131 credit clears >90 (1217) then 61-90 (390), and its 1524 remainder comes off the 1-30
 * debt → over60 = 0, under60 = 2816. Nothing over 60 days is open, so the account is not לגביה.
 *
 * The account total is preserved (`over60 + under60` is unchanged), only its age is.
 */
export const applyCreditsOldestFirst = ({
  over60Debt,
  over60Credit,
  under60Debt,
  under60Credit
}: AgingBuckets): AgingTotals => {
  const credit = over60Credit + under60Credit;
  return {
    over60: Math.max(0, over60Debt - credit),
    // Whatever the old debt didn't consume rolls forward onto the newer buckets; if it outlives
    // those too the account ends up negative — i.e. in זכות.
    under60: under60Debt - Math.max(0, credit - over60Debt)
  };
};

/**
 * The "לקוחות מחושבים" algorithm, over aged debt that has already been through
 * `applyCreditsOldestFirst`:
 * - debt still open beyond 60 days → לגביה
 * - nothing open beyond 60 days and the account is net negative → לקוח בזכות
 * - otherwise (no old debt, nothing left over) → לא לגביה
 */
export const computeCalculatedStatus = ({ over60, under60 }: AgingTotals): string => {
  if (over60 > 0) return COLLECTION_TO_COLLECT;
  return over60 + under60 < 0 ? COLLECTION_CREDIT : COLLECTION_NO_COLLECT;
};

/**
 * How much of a מחושבים account is actually collectible — the same rule that
 * derives the status above, expressed as a number rather than a label. This is
 * what the Collection screen shows as "סכום", NOT the account's total debt:
 * only debt older than 60 days is collected, and every credit on the account has
 * already been offset against it by `applyCreditsOldestFirst`.
 *
 * By construction it is > 0 exactly when `computeCalculatedStatus` returns לגביה,
 * and 0 for לא לגביה / לקוח בזכות.
 */
export const computeCollectibleAmount = ({ over60 }: AgingTotals): number => Math.max(0, over60);

// --- Site blocking (חסימת אתר) derived from the collection status ---

/** The two statuses that mean "this customer owes money" (positive amount). */
export const DEBTOR_COLLECTION_STATUSES: readonly string[] = [COLLECTION_AUTO, COLLECTION_TO_COLLECT];

export const isDebtorCollectionStatus = (status: string | null): boolean =>
  !!status && DEBTOR_COLLECTION_STATUSES.includes(status);

/**
 * Whether an import that computed `next` may write it over the customer's `current` status.
 *
 * גביה אוטומטית never overrides לגביה. The two come from different sheets — הו"ק / אשראי
 * produce גביה אוטומטית, לקוחות מחושבים produces לגביה — and a customer can legitimately be in
 * both: they pay by standing order *and* carry aged debt a rep is chasing. The chase is the fact
 * that matters, so a customer already לגביה is left completely alone by the הו"ק / אשראי upload
 * (collection status, סטטוס טיפול and סטטוס אתר all untouched); otherwise uploading it after the
 * מחושבים sheet would quietly close the rep's open work and unblock the site it blocked.
 *
 * Every other combination is a plain overwrite — the month's Excel is the source of truth for the
 * collection status, including the מחושבים sheet moving a customer out of לגביה once they paid.
 */
export const importMayOverrideCollection = (current: string | null, next: string): boolean =>
  !(next === COLLECTION_AUTO && current === COLLECTION_TO_COLLECT);

/**
 * The releasing half of the site rule: what to write once a customer's debt is closed — whether
 * they dropped out of the monthly מחושבים file entirely (`settleCustomersMissingFromFile`) or are
 * still in it with nothing left over 60 days (`siteStatusForCollectionStatus`).
 *
 * Only a site that was blocked for the debt is re-opened (לסגירה / סגור → לפתיחה), and לפתיחה is a
 * task for a rep, not an open site. A site already פתוח has nothing to open, and a VIP site is the
 * same explicit opt-out as in the blocking direction — its status is pinned by the tag, not the Excel.
 */
export const siteStatusForSettledDebt = (currentSiteStatus: string | null): string | null =>
  currentSiteStatus === SITE_STATUS_TO_CLOSE || currentSiteStatus === SITE_STATUS_CLOSED ? SITE_STATUS_TO_OPEN : null;

/**
 * The sheets whose status can block a site, and therefore the only ones allowed to lift a block.
 *
 * לגביה — the status that sends a customer to לסגירה — comes from exactly these two: the מחושבים
 * sheet (debt older than 60 days) and the לקוחות balance sheet. הו"ק / אשראי produce גביה אוטומטית
 * for a debtor, so a "no debt" row there says nothing about a block someone else set: a zero
 * balance in the credit-card sheet must not reopen a site closed for an unrelated reason.
 */
export const BLOCKING_EXCEL_TYPES: readonly string[] = [EXCEL_TYPE_CUSTOMERS, EXCEL_TYPE_CALCULATED];

export const excelTypeBlocksSite = (excelType: string): boolean => BLOCKING_EXCEL_TYPES.includes(excelType);

/**
 * The site status an Excel import should write for a customer, or `null` to leave it untouched:
 * - לגביה (a debt a rep has to chase) → לסגירה, i.e. the customer joins the "חסימות" list.
 * - גביה אוטומטית (הו"ק / אשראי — the debt collects itself) → פתוח: there is nothing for a rep
 *   to chase, so the site must not sit blocked over that debt.
 * - לא לגביה / לקוח בזכות, from a sheet that could have blocked → לפתיחה, so a site blocked over a
 *   debt that is now closed lands on the rep's "to open" list instead of sitting shut forever.
 *   From any other sheet → untouched.
 *
 * That third rule is the one that changed (2026-09-14). It used to leave the block alone, on the
 * grounds that lifting one is a human decision — but the human was never told: a customer still in
 * the file whose debt simply dropped below the 60-day line got no signal at all, and the only
 * accounts that ever reopened were the ones that vanished from the file (`siteStatusForSettledDebt`).
 * After the credit-offset fix moved hundreds of accounts out of לגביה at once, they all stayed shut.
 * לפתיחה is a task marker, not an open site, so the decision stays with the rep — they just get told.
 *
 * Two exceptions cut across all of it: a VIP site (לקוח VIP (לא לחסימה)) is pinned by its tag,
 * and a site already סגור is never walked back to לסגירה — it is closed already. Note that סגור *is*
 * opened by גביה אוטומטית: that is the customer who was blocked over a debt and has since signed a
 * הוראת קבע, which is exactly the case this rule exists for.
 */
export const siteStatusForCollectionStatus = (
  currentSiteStatus: string | null,
  collectionStatus: string,
  excelType: string
): string | null => {
  if (currentSiteStatus === SITE_STATUS_VIP) return null;
  if (collectionStatus === COLLECTION_AUTO) return currentSiteStatus === SITE_STATUS_OPEN ? null : SITE_STATUS_OPEN;
  if (collectionStatus !== COLLECTION_TO_COLLECT)
    return excelTypeBlocksSite(excelType) ? siteStatusForSettledDebt(currentSiteStatus) : null;
  if (currentSiteStatus === SITE_STATUS_CLOSED) return null;
  return currentSiteStatus === SITE_STATUS_TO_CLOSE ? null : SITE_STATUS_TO_CLOSE;
};

// --- רצף גביה (consecutive לגביה months) ---

/**
 * How many consecutive months, up to the customer's most recent collection month, a customer has
 * been "לגביה".
 *
 * Only `לגביה` counts: `גביה אוטומטית` breaks the streak just like `לא לגביה` and `לקוח בזכות` —
 * this measures how long a *rep* has been chasing the same debt, and an auto-collected account
 * isn't being chased. A month with no records at all is skipped rather than treated as a break:
 * missing data is not evidence the customer stopped owing.
 *
 * The same number on every screen: the ניהול גביה list is per-month, but this column is **not**
 * bounded by the selected month — otherwise one customer would read 1 there and 2 on the לקוחות
 * list. The cost is that an older month shows today's streak, not that month's.
 *
 * Computed in SQL per query (see `collectionStreak.sql.ts` on the backend), never stored.
 */

/** A streak of this many months or more is flagged red ("מעל 4 כולל 4"). */
export const COLLECTION_STREAK_ALERT = 4;

/**
 * The "רצף גביה" list filter is a checklist of exact month counts — ticking 2 and 4 lists the
 * customers whose streak is exactly 2 or exactly 4. The last one is open-ended ("6 חודשים ומעלה"),
 * so a longer streak is never filtered out of reach.
 */
export const COLLECTION_STREAK_FILTER_CAP = 6;
export const COLLECTION_STREAK_FILTER_MONTHS = Array.from({ length: COLLECTION_STREAK_FILTER_CAP }, (_, i) => i + 1);

export const collectionStreakLabel = (months: number): string => {
  if (months >= COLLECTION_STREAK_FILTER_CAP) return `${COLLECTION_STREAK_FILTER_CAP} חודשים ומעלה`;
  return months === 1 ? 'חודש אחד' : `${months} חודשים`;
};

/** The "משימות פתוחות" list filter — whether the customer has an open task in the month on screen. */
export const OPEN_TASKS_FILTER_VALUES = ['yes', 'no'] as const;
export const OPEN_TASKS_FILTER_OPTIONS = [
  { value: 'yes', label: 'יש משימות פתוחות' },
  { value: 'no', label: 'אין משימות פתוחות' }
];

// --- Entity (API shape) ---
export interface CollectionRecord {
  id: number;
  period: string; // 'YYYY-MM'
  excelType: string;
  priorityId: string;
  customerId: number | null; // null → unidentified (מספר לקוח שלא זוהה)
  customerName: string | null;
  phone: string | null;
  amount: number;
  amountOver60: number | null; // מחושבים בלבד — after the oldest-first credit offset
  amountUnder60: number | null; // מחושבים בלבד — after the oldest-first credit offset
  /**
   * "סכום חובה" — the account's whole balance net of credits, of any age, as opposed to the
   * collectible `amount` above. Derived per query by the list endpoint (`collectionAmounts.sql.ts`),
   * so it is present on the ניהול גביה list and absent from the customer-card history.
   */
  totalDebt?: number;
  computedStatus: string;
  /**
   * This month's treatment status — the record's own column, so היסטוריית גביה shows what happened
   * in each month rather than repeating the customer's current status. The customer's `treatmentStatus`
   * remains the current value; this is the one the ניהול גביה list shows and filters on.
   */
  treatmentStatus?: string | null;
  /**
   * The customer's current consecutive-"לגביה" month count — computed by the list query, and
   * deliberately independent of the selected month so it matches the לקוחות list.
   */
  collectionStreak?: number;
  /** Customer-derived dimension, surfaced for list filtering (the value lives under `customer`). */
  handlingUserId?: number | null;
  /** Customer-derived dimension, surfaced for list filtering (the value lives under `customer`). */
  siteStatus?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** Joined customer snapshot (identified rows in the list view). */
  customer?: {
    id: number;
    name: string;
    phone: string | null;
    email: string | null;
    treatmentStatus: string | null;
    /** Editable straight from the collection list (the "סטטוס אתר" column). */
    siteStatus: string | null;
    /** Carries the VIP system tag → the site status is locked, its dropdown read-only. */
    isVip?: boolean;
    /** How many notes the customer has — the list shows its הערות icon only when this is > 0. */
    notesCount?: number;
    /** The customer's tasks in the list's month — the משימות icon shows only when this is > 0. */
    tasksCount?: number;
    /** Of those, the ones not yet marked טופל. */
    openTasksCount?: number;
    /** The customer's tags, shown read-only in the list's "תגיות" column (edited from the customer card). */
    tags?: CustomerTag[];
    handlingUser?: { id: number; firstName: string; lastName: string } | null;
  } | null;
}

// --- Import batches (יומן העלאות + ביטול) ---

/** Lifecycle of a single Excel upload. Only an `active` batch can be undone. */
export const IMPORT_BATCH_ACTIVE = 'active';
export const IMPORT_BATCH_SUPERSEDED = 'superseded';
export const IMPORT_BATCH_UNDONE = 'undone';

/** One monthly Excel upload — the unit an "undo" reverses. */
export interface CollectionImportBatch {
  id: number;
  period: string; // 'YYYY-MM'
  excelType: string;
  userId: number | null;
  importedCount: number;
  updatedCount: number;
  unidentifiedCount: number;
  status: string; // IMPORT_BATCH_*
  createdAt: string;
  /** Whether this batch is the current, reversible upload for its period+type. */
  canUndo?: boolean;
  /** Who uploaded it (joined for display). */
  user?: { id: number; firstName: string; lastName: string } | null;
}

// --- Overview dashboard (תמונת מצב) ---

export interface OverviewPie {
  label: string;
  value: number;
}

/** Amount metrics for one period (the monthly graph + the selected-month KPIs). */
export interface OverviewMonthlyPoint {
  period: string;
  totalDebtors: number; // Σ amount > 0
  treatmentToHandle: number; // treatmentStatus = לטיפול
  treatmentDone: number; // treatmentStatus = חוב טופל
  credit: number; // excelType = אשראי
  horaatKeva: number; // excelType = הו"ק
  /**
   * Total debt of the שלוחים accounts (excelType = לקוחות מחושבים), summed over **all** aging
   * buckets — deliberately not the collectible figure `amount` holds, which counts only debt older
   * than 60 days. Accounts whose total is a credit are left out, so a customer in זכות doesn't
   * shrink someone else's debt (same rule as `totalDebtors`).
   */
  shluchimDebt: number;
}

export type OverviewAmounts = Omit<OverviewMonthlyPoint, 'period'>;

export interface OverviewRepRow {
  userId: number;
  name: string;
  linkedCount: number; // customers linked to the rep (current base)
  toCollectCount: number; // of those, collection = לגביה
  collectAmount: number; // selected-month Σ amount where computedStatus ∈ {לגביה, גביה אוטומטית}
  treatedAmount: number; // selected-month Σ amount where treatmentStatus ∈ {חוב טופל, נחתם הוראת קבע}
}

export interface OverviewData {
  period: string;
  totalCustomers: number;
  pies: {
    collection: OverviewPie[];
    paymentMethod: OverviewPie[];
    treatmentStatus: OverviewPie[];
    siteStatus: OverviewPie[];
  };
  amounts: OverviewAmounts;
  monthly: OverviewMonthlyPoint[];
  reps: OverviewRepRow[];
}
